import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function TipPage() {
    const [bill, setBill] = useState(85.5);
    const [tipPct, setTipPct] = useState(18);
    const [people, setPeople] = useState(2);

    const result = useMemo(() => {
        const b = toNum(bill);
        const tip = b * (toNum(tipPct) / 100);
        const total = b + tip;
        const per = Math.max(1, toNum(people));
        return { tip, total, perPerson: total / per };
    }, [bill, tipPct, people]);

    const presets = [10, 15, 18, 20, 22, 25];

    return (
        <CalculatorShell
            slug="tip"
            explainer={
                                <>
                    <h2>How this tip calculator works</h2>
                    <p>
                        A tip calculator figures out the tip amount, total bill, and per-person split for any
                        bill size and tip percentage. Use it for restaurant meals, rideshares, hotel stays, or
                        any service where tipping applies.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Tip amount:</strong> subtotal × tip percentage — enter any percentage to see the exact dollar amount.</li>
                        <li><strong>Standard rates:</strong> 15–20% is typical for restaurants; 10–15% for other services.</li>
                        <li><strong>Total bill:</strong> subtotal plus tip — what you actually hand over or charge to your card.</li>
                        <li><strong>Per-person split:</strong> total divided by number of people in the party — rounds to the nearest cent.</li>
                        <li><strong>Pre-tax vs. post-tax tipping:</strong> you can tip on either — tipping on the pre-tax subtotal is technically standard but both are common.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Tip based on service quality, not food quality — the server didn't cook your meal.</li>
                        <li>For large groups, check the bill for an automatic gratuity before adding a second tip.</li>
                        <li>For delivery, factor in any platform fees the driver may not receive — tip the driver directly when possible.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Bill total" prefix="$" value={bill} onChange={setBill} step={0.01} dataTestId="calc-input-bill" />
                    <div>
                        <Field label="Tip %" suffix="%" value={tipPct} onChange={setTipPct} dataTestId="calc-input-tip" />
                        <div className="flex flex-wrap gap-2 mt-2">
                            {presets.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setTipPct(p)}
                                    data-testid={`tip-preset-${p}`}
                                    className={`h-9 px-3 rounded-full border text-xs font-mono tabular transition-colors ${
                                        toNum(tipPct) === p
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "border-border text-primary hover:border-primary/40"
                                    }`}
                                >
                                    {p}%
                                </button>
                            ))}
                        </div>
                    </div>
                    <Field label="Split between" value={people} onChange={setPeople} suffix={toNum(people) === 1 ? "person" : "people"} dataTestId="calc-input-people" />
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Per person"
                        value={result.perPerson}
                        format={(n) => fmtUSD(n, { decimals: 2 })}
                        secondary={[
                            { label: "Tip", value: result.tip, format: (n) => fmtUSD(n, { decimals: 2 }), testId: "calc-result-tip" },
                            { label: "Total", value: result.total, format: (n) => fmtUSD(n, { decimals: 2 }), testId: "calc-result-total" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
